import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { UsersService } from './users.service';
import { ApiKeyInfo, User } from './users.type';
import { UserRole } from '../auth/auth.type';
import { UserFormDialog } from './user-form-dialog/user-form-dialog';
import { ApiKeyDialog } from './api-key-dialog/api-key-dialog';
import { ManageRolesDialog } from './manage-roles-dialog/manage-roles-dialog';
import { ManagePermissionsDialog } from './manage-permissions-dialog/manage-permissions-dialog';
import { ConfirmDialog } from '../shared/confirm-dialog/confirm-dialog';
import { DangerConfirmDialog } from '../shared/danger-confirm-dialog/danger-confirm-dialog';
import { Header } from '../shared/header/header';
import { AuthService } from '../auth/auth.service';
import { SupplierInfoDialog } from '../supplier/supplier-info-dialog/supplier-info-dialog';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-users',
  imports: [
    CommonModule,
    FormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    MatTooltipModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatPaginatorModule,
    Header
  ],
  templateUrl: './users.html',
  styleUrl: './users.scss'
})
export class Users implements OnInit {
  private usersService = inject(UsersService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private authService = inject(AuthService);

  displayedColumns: string[] = ['username', 'name', 'roles', 'status', 'created_at', 'action'];
  users: User[] = [];
  /** user_id → key metadata, for every user that currently has an API key. */
  apiKeys = new Map<string, ApiKeyInfo>();
  loading: boolean = true;
  totalUsers: number = 0;

  // Filters
  filterRole: string = '';
  filterSearch: string = '';

  // Pagination
  pageIndex = 0;
  pageSize = 20;
  readonly pageSizeOptions = [10, 20, 50, 100];

  private search$ = new Subject<void>();

  readonly allRoles: UserRole[] = [
    'SuperAdmin', 'Admin', 'Supplier', 'Consumer', 'AuctionAccess'
  ];

  constructor() {
    this.search$.pipe(debounceTime(300), takeUntilDestroyed()).subscribe(() => {
      this.pageIndex = 0;
      this.loadUsers();
    });
  }

  ngOnInit(): void {
    this.loadUsers();
    this.loadApiKeys();
  }

  loadApiKeys(): void {
    this.usersService.listApiKeys().subscribe({
      next: (response) => {
        this.apiKeys = new Map((response.result.keys ?? []).map(k => [k.user_id, k]));
      },
      error: (error) => {
        console.error('Failed to load API keys:', error);
        this.snackBar.open('Error loading API keys', 'Close', { duration: 5000 });
      }
    });
  }

  loadUsers(): void {
    this.loading = true;
    this.usersService.getUsers({
      role: this.filterRole || undefined,
      search: this.filterSearch.trim() || undefined,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
    }).subscribe({
      next: (response) => {
        this.users = response.users ?? [];
        this.totalUsers = response.total;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load users:', error);
        this.snackBar.open('Error loading users', 'Close', { duration: 5000 });
        this.loading = false;
      }
    });
  }

  onRoleChange(): void {
    this.pageIndex = 0;
    this.loadUsers();
  }

  onSearchChange(): void {
    this.search$.next();
  }

  onPage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadUsers();
  }

  clearFilters(): void {
    this.filterRole = '';
    this.filterSearch = '';
    this.pageIndex = 0;
    this.loadUsers();
  }

  openSupplierInfoDialog(user: User): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(SupplierInfoDialog, {
      data: { supplierId: user.user_id, supplierUsername: user.username },
      width: isMobile ? '100vw' : undefined,
      minWidth: isMobile ? undefined : '420px',
      maxWidth: isMobile ? '100vw' : '95vw',
      maxHeight: '90vh',
    });
  }

  openCreateUserDialog(): void {
    const dialogRef = this.dialog.open(UserFormDialog, {
      width: '500px',
      maxWidth: '95vw',
      disableClose: true,
      data: { mode: 'create' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadUsers();
    });
  }

  openEditUserDialog(user: User): void {
    const dialogRef = this.dialog.open(UserFormDialog, {
      width: '500px',
      maxWidth: '95vw',
      disableClose: true,
      data: { mode: 'edit', user }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.loadUsers();
    });
  }

  openManageRolesDialog(user: User): void {
    const dialogRef = this.dialog.open(ManageRolesDialog, {
      width: '480px',
      maxWidth: '95vw',
      disableClose: false,
      data: { user }
    });

    dialogRef.afterClosed().subscribe(rolesChanged => {
      if (rolesChanged) this.loadUsers();
    });
  }

  openManagePermissionsDialog(user: User): void {
    this.dialog.open(ManagePermissionsDialog, {
      width: '560px',
      maxWidth: '95vw',
      data: { user }
    }).afterClosed().subscribe(changed => {
      if (changed) this.loadUsers();
    });
  }

  deleteUser(user: User): void {
    const dialogRef = this.dialog.open(DangerConfirmDialog, {
      maxWidth: 'unset',
      disableClose: true,
      data: {
        title: `Delete "${user.username}"`,
        warnings: [
          'This PERMANENTLY deletes the user account — this cannot be undone.',
          'It also deletes EVERYTHING tied to this user across the whole app: supplier profile, stocks, sell/buy contracts, settlements, EA game accounts they created, and more.',
          'If any of that data is still shared with another user (e.g. a contract account someone else is using), the deletion is blocked entirely and nothing is removed.',
        ],
        confirmText: user.username,
        confirmLabel: 'exact username',
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) return;

      this.usersService.deleteUser(user.user_id, { username: user.username, confirm: true }).subscribe({
        next: (response) => {
          const removed = Object.entries(response.deleted)
            .filter(([, count]) => count > 0)
            .map(([table, count]) => `${count} ${table}`)
            .join(', ');
          this.snackBar.open(
            removed ? `User deleted. Also removed: ${removed}` : 'User deleted successfully',
            'Close',
            { duration: 8000 }
          );
          // Don't strand the view on a page that just became empty.
          if (this.users.length === 1 && this.pageIndex > 0) this.pageIndex--;
          this.loadUsers();
        },
        error: (error) => {
          console.error('Failed to delete user:', error);
          const message = error.error?.error || 'Error deleting user';
          this.snackBar.open(message, 'Close', { duration: 8000 });
        }
      });
    });
  }

  apiKeyTooltip(user: User): string {
    const key = this.apiKeys.get(user.user_id);
    if (!key) return 'Generate API key';
    const lastUsed = key.last_used_at ? `last used ${new Date(key.last_used_at).toLocaleString()}` : 'never used';
    return `Revoke API key (${lastUsed})`;
  }

  toggleApiKey(user: User): void {
    if (!this.apiKeys.has(user.user_id)) {
      this.generateApiKey(user);
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialog, {
      maxWidth: 'unset',
      data: {
        title: 'Revoke API Key',
        message: `Revoke the API key of "${user.username}"? Anything using it will stop working immediately.`
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.revokeApiKey(user);
    });
  }

  private generateApiKey(user: User): void {
    this.usersService.generateApiKey(user.user_id).subscribe({
      next: (response) => {
        this.loadApiKeys();
        this.dialog.open(ApiKeyDialog, {
          width: '520px',
          maxWidth: '95vw',
          disableClose: true,
          data: { username: user.username, apiKey: response.result.api_key ?? '' }
        });
      },
      error: (error) => {
        const message = error.error?.error || 'Error generating API key';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      }
    });
  }

  private revokeApiKey(user: User): void {
    this.usersService.revokeApiKey(user.user_id).subscribe({
      next: () => {
        this.apiKeys.delete(user.user_id);
        this.snackBar.open('API key revoked', 'Close', { duration: 5000 });
      },
      error: (error) => {
        const message = error.error?.error || 'Error revoking API key';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      }
    });
  }

  isSuperAdmin(user: User): boolean {
    return user.roles?.includes('SuperAdmin') ?? false;
  }

  isSelf(user: User): boolean {
    return user.user_id === this.authService.getCurrentUser()?.user_id;
  }

  isAdmin(user: User): boolean {
    return user.roles?.includes('Admin') ?? false;
  }

  isSupplier(user: User): boolean {
    return user.roles?.includes('Supplier') ?? false;
  }

  /**
   * Opens the supplier portal in a new tab, signed in as this user, via a
   * short-lived (1h) impersonation token minted server-side. Lets a
   * SuperAdmin see the supplier panel exactly as the supplier would without
   * ever touching their real password.
   */
  viewAsSupplier(user: User): void {
    this.usersService.impersonate(user.user_id).subscribe({
      next: (response) => {
        const url = `${environment.supplierFrontURL}/impersonate?token=${encodeURIComponent(response.token)}`;
        window.open(url, '_blank', 'noopener');
      },
      error: (error) => {
        const message = error.error?.error || 'Error starting impersonation';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      }
    });
  }

  toggleActiveStatus(user: User): void {
    if (user.active) {
      const dialogRef = this.dialog.open(ConfirmDialog, {
        maxWidth: 'unset',
        data: {
          title: 'Block User',
          message: `Are you sure you want to block "${user.username}"? They will be signed out immediately and unable to log in until reactivated.`
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) this.setActiveStatus(user, false);
      });
    } else {
      this.setActiveStatus(user, true);
    }
  }

  private setActiveStatus(user: User, active: boolean): void {
    this.usersService.updateUserActive(user.user_id, active).subscribe({
      next: (updated) => {
        user.active = updated.active;
        this.snackBar.open(active ? 'User activated' : 'User blocked', 'Close', { duration: 5000 });
      },
      error: (error) => {
        const message = error.error?.error || (active ? 'Error activating user' : 'Error blocking user');
        this.snackBar.open(message, 'Close', { duration: 5000 });
      }
    });
  }

  getRoleClass(role: UserRole): string {
    switch (role) {
      case 'SuperAdmin': return 'role-super-admin';
      case 'Admin': return 'role-admin';
      case 'Supplier': return 'role-supplier';
      case 'Consumer': return 'role-consumer';
      default: return 'role-feature';
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }
}
